/*
  SBD: back-in-stock alerts.

  Markup: snippets/sbd-back-in-stock.liquid. Styles: assets/sbd-back-in-stock.css.

  The shopper picks a sold-out variant and enters an email. That is posted to
  Klaviyo's client back-in-stock endpoint, which records "Subscribed to Back in
  Stock" on their profile and puts them in the Klaviyo back-in-stock flow. If
  they tick the news box, a second call subscribes them to the list in
  data-list-id. The alert itself never adds anyone to a marketing list.
*/
(function () {
  'use strict';

  var KLAVIYO = 'https://a.klaviyo.com/client';
  var REVISION = '2026-10-15';
  var HELP_EMAIL = 'service@sbdapparel.co.nz';

  /* ---------- Pure helpers (also used by the tests) ---------- */

  // Does a variant match every option that has been chosen so far?
  // Unchosen options (null) match anything.
  function matches(variant, selected) {
    for (var i = 0; i < selected.length; i++) {
      if (selected[i] != null && variant.options[i] !== selected[i]) return false;
    }
    return true;
  }

  // The variant for a complete selection, or null.
  function findVariant(variants, selected) {
    for (var i = 0; i < selected.length; i++) {
      if (selected[i] == null) return null;
    }
    for (var j = 0; j < variants.length; j++) {
      if (matches(variants[j], selected)) return variants[j];
    }
    return null;
  }

  // State of one option value, given the options chosen before it.
  // Later options are ignored, so a fit shows as sold out if any size in
  // that fit is sold out.
  //   'out'  at least one matching variant is sold out (can be alerted)
  //   'in'   every matching variant is in stock
  //   'none' no variant has this combination
  function valueState(variants, selected, index, value) {
    var trial = [];
    for (var i = 0; i < selected.length; i++) {
      trial.push(i < index ? selected[i] : null);
    }
    trial[index] = value;

    var found = false;
    for (var j = 0; j < variants.length; j++) {
      if (matches(variants[j], trial)) {
        found = true;
        if (!variants[j].available) return 'out';
      }
    }
    return found ? 'in' : 'none';
  }

  // What the modal opens with. If the shopper is already looking at a
  // sold-out variant, use it. Otherwise carry over the earlier options (fit,
  // colour) from the page and leave the last one (size) for them to choose,
  // so nobody is signed up for a size they didn't pick.
  function initialSelection(variants, optionCount, pageSelected) {
    var page = pageSelected || [];
    var selected = [];
    for (var i = 0; i < optionCount; i++) selected.push(null);

    var pageVariant = findVariant(variants, page.length === optionCount ? page : selected);
    if (pageVariant && !pageVariant.available) return page.slice();

    for (var k = 0; k < optionCount - 1; k++) {
      if (page[k] == null || valueState(variants, selected, k, page[k]) === 'none') break;
      selected[k] = page[k];
    }
    return selected;
  }

  // After choosing an option, drop any later choices that no longer exist.
  function choose(variants, selected, index, value) {
    var next = selected.slice();
    next[index] = value;
    for (var i = index + 1; i < next.length; i++) {
      if (next[i] != null && valueState(variants, next, i, next[i]) === 'none') next[i] = null;
    }
    return next;
  }

  // "M", "Men's M", "Medium Stiff". Empty for single-variant products.
  function sizeLabel(variant, defaultOnly) {
    if (!variant || defaultOnly) return '';
    return variant.options.join(' ');
  }

  function bisPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          channels: ['EMAIL']
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
        }
      }
    };
  }

  function listPayload(email, listId) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock alert',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
  }

  function errorMessage(err) {
    if (err && err.status === 400) {
      var detail = '';
      try { detail = err.body.errors[0].detail || ''; } catch (e) { /* no body */ }
      if (/email/i.test(detail)) return 'Check your email address and try again.';
    }
    if (err && err.status) {
      return 'That didn’t go through. Please try again, or email ' + HELP_EMAIL + '.';
    }
    return 'That didn’t go through. Check your connection and try again.';
  }

  var helpers = {
    matches: matches,
    findVariant: findVariant,
    valueState: valueState,
    initialSelection: initialSelection,
    choose: choose,
    sizeLabel: sizeLabel,
    bisPayload: bisPayload,
    listPayload: listPayload,
    errorMessage: errorMessage
  };
  if (typeof module === 'object' && module.exports) module.exports = helpers;

  /* ---------- The element ---------- */

  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('sbd-back-in-stock')) return;

  function post(companyId, path, body) {
    return fetch(KLAVIYO + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return res;
      return res.json().catch(function () { return {}; }).then(function (body) {
        var err = new Error('Klaviyo ' + res.status);
        err.status = res.status;
        err.body = body;
        throw err;
      });
    });
  }

  class SbdBackInStock extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.companyId = this.dataset.companyId;
      this.listId = this.dataset.listId || '';
      this.formId = this.dataset.formId;
      this.productTitle = this.dataset.productTitle;
      this.defaultOnly = this.hasAttribute('data-default-only');

      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-bis-form]');
      this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-bis-group]'));
      this.statusEl = this.querySelector('[data-bis-status]');
      this.keyEl = this.querySelector('[data-bis-key]');
      this.emailEl = this.querySelector('[data-bis-email]');
      this.marketingEl = this.querySelector('[data-bis-marketing]');
      this.errorEl = this.querySelector('[data-bis-error]');
      this.submitEl = this.querySelector('[data-bis-submit]');
      this.doneEl = this.querySelector('[data-bis-done]');

      try {
        this.variants = JSON.parse(this.querySelector('[data-bis-variants]').textContent);
      } catch (e) {
        this.hidden = true;
        return;
      }
      this.optionCount = this.variants.length ? this.variants[0].options.length : 0;
      this.labels = this.groups.map(function (g) {
        return g.querySelector('legend').textContent.trim().toLowerCase();
      });
      this.selected = [];

      var self = this;
      this.querySelector('[data-bis-open]').addEventListener('click', function () { self.open(); });
      Array.prototype.forEach.call(this.querySelectorAll('[data-bis-close]'), function (btn) {
        btn.addEventListener('click', function () { self.dialog.close(); });
      });
      // Clicking the dim background closes the modal.
      this.dialog.addEventListener('click', function (e) {
        if (e.target === self.dialog) self.dialog.close();
      });
      this.groups.forEach(function (group, index) {
        group.addEventListener('change', function (e) {
          if (!e.target.matches('input[type="radio"]')) return;
          self.selected = choose(self.variants, self.selected, index, e.target.value);
          self.hideError();
          self.render();
        });
      });
      // Keep these events inside the modal so the theme's own variant
      // picker never reacts to them.
      this.addEventListener('change', function (e) { e.stopPropagation(); });
      this.addEventListener('input', function (e) { e.stopPropagation(); });
      this.form.addEventListener('submit', function (e) { self.submit(e); });
    }

    // Options currently ticked in the theme's own size picker.
    pageSelection() {
      var selected = [];
      for (var i = 0; i < this.optionCount; i++) selected.push(null);
      if (!this.formId) return selected;
      var inputs = document.querySelectorAll('input[data-variant-input]:checked');
      Array.prototype.forEach.call(inputs, function (input) {
        if (input.getAttribute('form') !== this.formId) return;
        var index = parseInt(String(input.dataset.index || '').replace('option', ''), 10) - 1;
        if (index >= 0 && index < selected.length) selected[index] = input.value;
      }, this);
      return selected;
    }

    open() {
      this.form.hidden = false;
      this.doneEl.hidden = true;
      this.hideError();
      this.setBusy(false);

      if (this.defaultOnly) {
        this.selected = this.variants[0] ? this.variants[0].options.slice() : [];
      } else {
        this.selected = initialSelection(this.variants, this.optionCount, this.pageSelection());
      }
      this.render();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      // Focus the first choice still to make, otherwise the email field.
      var target = null;
      for (var i = 0; i < this.groups.length && !target; i++) {
        if (this.selected[i] == null) {
          target = this.groups[i].querySelector('input:not(:disabled)');
        }
      }
      (target || this.emailEl).focus();
    }

    render() {
      var self = this;
      var lastIndex = this.groups.length - 1;
      var lastHasInStock = false;

      this.groups.forEach(function (group, index) {
        Array.prototype.forEach.call(group.querySelectorAll('input[type="radio"]'), function (input) {
          var state = valueState(self.variants, self.selected, index, input.value);
          var label = input.closest('.sbd-bis__value');
          var hint = label.querySelector('[data-bis-hint]');
          input.disabled = state === 'none';
          input.checked = self.selected[index] === input.value;
          label.setAttribute('data-state', state);
          if (hint) hint.textContent = state === 'in' ? ' (in stock)' : state === 'none' ? ' (not available)' : '';
          if (index === lastIndex && state === 'in') lastHasInStock = true;
        });
      });
      if (this.keyEl) this.keyEl.hidden = !lastHasInStock;

      var variant = findVariant(this.variants, this.selected);
      var size = sizeLabel(variant, this.defaultOnly);
      var status = '';

      if (!variant) {
        var missing = [];
        for (var i = 0; i < this.labels.length; i++) {
          if (this.selected[i] == null) missing.push(this.labels[i]);
        }
        status = 'Choose your ' + missing.join(' and ') + '.';
      } else if (variant.available) {
        status = (size || 'This') + ' is in stock now. Close this and add it to your cart.';
      } else {
        status = (size || 'This') + ' is sold out. We’ll email you when it’s back.';
      }

      this.statusEl.textContent = status;
      this.statusEl.setAttribute('data-state', variant ? (variant.available ? 'in' : 'out') : 'choose');
      this.submitEl.disabled = !variant || variant.available;
    }

    submit(e) {
      e.preventDefault();
      if (this.busy) return;

      var variant = findVariant(this.variants, this.selected);
      if (!variant || variant.available) return;

      var email = this.emailEl.value.trim();
      this.emailEl.value = email;
      if (!email || !this.emailEl.checkValidity()) {
        this.showError('Enter a valid email address.');
        this.emailEl.focus();
        return;
      }

      var self = this;
      var wantsNews = !!(this.marketingEl && this.marketingEl.checked && this.listId);
      this.hideError();
      this.setBusy(true);

      post(this.companyId, '/back-in-stock-subscriptions/', bisPayload(email, variant.id))
        .then(function () {
          if (!wantsNews) return null;
          // The alert is already saved, so a problem here shouldn't undo it.
          return post(self.companyId, '/subscriptions/', listPayload(email, self.listId))
            .catch(function (err) {
              if (window.console) console.warn('SBD back in stock: list sign-up failed', err);
            });
        })
        .then(function () {
          self.showDone(email, variant);
        })
        .catch(function (err) {
          self.showError(errorMessage(err));
        })
        .then(function () {
          self.setBusy(false);
        });
    }

    showDone(email, variant) {
      var size = sizeLabel(variant, this.defaultOnly);
      var what = 'the ' + this.productTitle + (size ? ' in ' + size : '');
      this.querySelector('[data-bis-done-text]').textContent =
        'We’ll email ' + email + ' when ' + what + ' is back in stock.';
      this.form.hidden = true;
      this.doneEl.hidden = false;
      this.querySelector('[data-bis-done-title]').focus();
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    setBusy(busy) {
      this.busy = busy;
      this.submitEl.textContent = busy ? 'Sending…' : 'Notify me';
      this.submitEl.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        this.submitEl.disabled = true;
      } else if (this.variants) {
        var variant = findVariant(this.variants, this.selected);
        this.submitEl.disabled = !variant || variant.available;
      }
    }
  }

  window.customElements.define('sbd-back-in-stock', SbdBackInStock);
})();
